import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { Observable, catchError, map, of } from 'rxjs';
import { User } from 'src/app/shared/models/gql/user.model';
import { JobFromClient } from '../models/gql/JobFromClient.model';
import { Consumer } from '../models/gql/consumer.model';
import { ApiInterfaceService } from './api-interface.service';
import { BaseService } from './base.service';

/**
 * Provides CRUD interface for consumers. To get the current consumers
 * in a component, call list() and refresh() when necessary.
 */
@Injectable({
  providedIn: 'root',
})
export class ConsumersService extends BaseService {
  constructor(private api: ApiInterfaceService) {
    super({ acctScoped: true, resource: 'consumers', paginated: false });
  }

  /**
   * Gets a consumer by ID directly from the backend
   *
   */
  async get(): Promise<Consumer[]> {
    try {
      const res = await this.api.gqlQuery(gql`
        query {
          conversations {
            nodes {
              id
              otherUser {
                avatar {
                  small
                }
                profile {
                  ... on ClientProfile {
                    typeOfCare
                  }
                }
                firstName
                displayName
                id
                isHcpFunded
                ndisAgencyManaged
                disablePerKmTravel
                ndisNonNdia
                accountType
                uuid
                organisation {
                  businessName
                  id
                }
                coordinator {
                  truncatedName
                }
                ndisPlan {
                  ndisNumber
                  id
                  reviewDate
                  startDate
                  supportAreasAttributes {
                    id
                    name
                    supportItemsAttributes {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      `);
      return res['conversations']['nodes'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Gets a consumer by ID directly from the backend
   *
   * @param id - : consumer id to fetch open jobs
   */
  async getById(id: number): Promise<Consumer> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query user($id: ID) {
            user(id: $id) {
              id
              displayName
              address
              age
              avatar {
                small
              }
              permalink
              contactPhone
              accountType
              profile {
                ... on ClientProfile {
                  typeOfCare
                  ndisNumber
                  participantRelationship
                  fundingSourceCode
                }
              }
              coordinator {
                truncatedName
              }
              organisation {
                id
                businessName
              }
              ndisPlan {
                id
                ndisNumber
                reviewDate
                startDate
                supportAreasAttributes {
                  id
                  name
                  supportItemsAttributes {
                    name
                    id
                  }
                }
              }
              isHcpFunded
              ndisAgencyManaged
              disablePerKmTravel
              uuid
              ndisNonNdia
            }
          }
        `,
        { id },
      );
      return res['user'];
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }

  /**
   * Get open jobs based on consumer id
   *
   * @param id - : consumer id to fetch open jobs
   */
  async openJobs(id: number): Promise<JobFromClient[]> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query sharedOrRepliedJobsByClient($clientId: ID!) {
            sharedOrRepliedJobsByClient(clientId: $clientId) {
              id
              jobDescription
              client {
                displayName
                avatar {
                  small
                }
                profile {
                  ... on ClientProfile {
                    typeOfCare
                  }
                }
              }
              status {
                workerStatus
              }
              serviceCategories {
                name
              }
              jobKind {
                kind
              }
              participant {
                age
              }
              title
              frequency
              workerFields {
                jobShared {
                  sentAt
                  type
                }
              }
            }
          }
        `,
        { clientId: id },
      );
      return res['sharedOrRepliedJobsByClient'];
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }

  /**
   * Sends a GET request for fetching client details if they have enough SPs in their suburb. This is set
   * to 10.
   *
   * @returns An Observable containing true or false.
   */
  getEnoughCarersInSuburb(): Observable<boolean> {
    return this.api
      .gqlQuery$<User>(gql`
        {
          user {
            profile {
              ... on ClientProfile {
                enoughCarersInSuburb
              }
            }
          }
        }
      `)
      .pipe(
        map((user) => user?.profile?.enoughCarersInSuburb),
        catchError((error) => {
          // We only need to log. If it fails we just wont show to the user (i.e. return false)
          console.error(error);
          return of(false);
        }),
      );
  }
}
